import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  EditOutlined,
  PlusOutlined,
  PrinterOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import {
  useChangeTmmdeStatus,
  useRecordExternalVerification,
  useTmmdeEquipment,
  useTmmdeEquipmentUsage,
  useTmmdeImpactAssessments,
} from '@/hooks/useCalibration';
import type {
  CalibrationReading,
  CalibrationRecord,
  CalibrationSchedule,
  TmmdeEquipmentRow,
  TmmdeExternalVerification,
  TmmdeHoldCause,
  TmmdeImpactAssessment,
  TmmdeStatusAction,
  TmmdeUsageEntry,
} from '@/services/calibration.service';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_TMMDE_EXTERNAL_VERIFICATION } from '@/constants/document-entity-types';
import { TmmdeEquipmentLabelPrint } from '@/components/print/TmmdeEquipmentLabelPrint';
import type { TmmdeLabelVariant } from '@/print-engine/adapters/tmmde-label-adapter';
import { formatDate, formatDateTime } from '@/utils/format/date';
import {
  CALIBRATION_KIND_OPTIONS,
  HOLD_CAUSE_OPTIONS,
  IDENTIFICATION_METHOD_OPTIONS,
  IMPACT_TRIGGER_LABEL,
  INTERVAL_BASIS_OPTIONS,
  OWNERSHIP_OPTIONS,
  isCompanyOwned,
  optionLabel,
  tmmdeStatusColor,
  tmmdeStatusLabel,
  USAGE_SOURCE_LABEL,
  usageSourceLink,
} from './tmmdeDisplay';

const { Title, Text } = Typography;
const { TextArea } = Input;

const REVIEW_COLORS: Record<string, string> = { PENDING: 'gold', ACCEPTED: 'green', REJECTED: 'red' };
const RESULT_COLORS: Record<string, string> = { Pass: 'green', Fail: 'red', Conditional: 'orange', Pending: 'default' };

const ACTION_COPY: Record<TmmdeStatusAction, { title: string; reasonRequired: boolean; okText: string }> = {
  NOT_FOR_USE: { title: 'Mark equipment NOT FOR USE', reasonRequired: true, okText: 'Mark not for use' },
  QUARANTINE: { title: 'Quarantine equipment', reasonRequired: true, okText: 'Quarantine' },
  RELEASE: { title: 'Release equipment for use', reasonRequired: false, okText: 'Release' },
};

const readingColumns: ColumnsType<CalibrationReading> = [
  { title: 'Test point', dataIndex: 'test_point', render: (v) => v || '—' },
  { title: 'Nominal', dataIndex: 'nominal_value', render: (v) => v || '—' },
  { title: 'Tolerance', dataIndex: 'tolerance', render: (v) => v || '—' },
  { title: 'As found', dataIndex: 'as_found', render: (v) => v || '—' },
  { title: 'As left', dataIndex: 'as_left', render: (v) => v || '—' },
  { title: 'Unit', dataIndex: 'unit', render: (v) => v || '—' },
  {
    title: 'Result',
    dataIndex: 'result',
    render: (v) => (v ? <Tag color={v === 'PASS' ? 'green' : 'red'}>{v}</Tag> : '—'),
  },
];

const TmmdeEquipmentDetailPage: React.FC = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const has = (code: string) => Boolean(user?.is_super_admin || user?.permissions?.includes(code));
  const canChangeStatus = has('calibration.equipment.status');
  const canManage = has('calibration.equipment.manage');
  const canEditSchedule = has('calibration.schedule.edit');
  const canCreateSchedule = has('calibration.schedule.create');
  const canCreateRecord = has('calibration.record.create');

  const { data, isLoading, error } = useTmmdeEquipment(assetId);
  const { data: usage = [], isLoading: usageLoading } = useTmmdeEquipmentUsage(assetId);
  const statusMutation = useChangeTmmdeStatus();
  const verifyMutation = useRecordExternalVerification();

  const [action, setAction] = useState<TmmdeStatusAction | null>(null);
  const [reason, setReason] = useState('');
  const [cause, setCause] = useState<TmmdeHoldCause>('OUT_OF_CALIBRATION');
  const canViewImpact = has('calibration.impact.view') || has('calibration.impact.manage');
  const { data: impactAssessments = [] } = useTmmdeImpactAssessments({ asset_id: assetId }, Boolean(assetId) && canViewImpact);
  const [labelVariant, setLabelVariant] = useState<TmmdeLabelVariant | null>(null);
  const [verifyForm] = Form.useForm();

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div style={{ padding: 24 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/tmmde')}>
          Equipment List
        </Button>
        <Alert type="error" showIcon style={{ marginTop: 16 }} message="Measuring equipment not found" />
      </div>
    );
  }

  const eq = data.equipment;
  const activeSchedule: CalibrationSchedule | undefined = data.schedules.find((s) => s.status !== 'Cancelled');
  const onHold = eq.calibration_status === 'NOT_FOR_USE' || eq.calibration_status === 'QUARANTINED';
  const external = !isCompanyOwned(eq.equipment_ownership);

  if (labelVariant) {
    return (
      <TmmdeEquipmentLabelPrint equipment={eq} variant={labelVariant} onClose={() => setLabelVariant(null)} />
    );
  }

  const submitAction = async () => {
    if (!action) return;
    if (ACTION_COPY[action].reasonRequired && !reason.trim()) return;
    await statusMutation.mutateAsync({
      assetId: eq.id,
      action,
      reason: reason.trim() || undefined,
      cause: action === 'NOT_FOR_USE' ? cause : undefined,
    });
    setAction(null);
    setReason('');
  };

  const submitVerification = async (v: {
    physical_condition_ok?: boolean;
    physical_condition_notes?: string;
    certificate_verified?: boolean;
    certificate_reference?: string;
    certificate_valid_until?: Dayjs | null;
    suitable_for_use?: boolean;
    limitations?: string;
    remarks?: string;
  }) => {
    await verifyMutation.mutateAsync({
      assetId: eq.id,
      data: {
        physical_condition_ok: Boolean(v.physical_condition_ok),
        physical_condition_notes: v.physical_condition_notes || null,
        certificate_verified: Boolean(v.certificate_verified),
        certificate_reference: v.certificate_reference || null,
        certificate_valid_until: v.certificate_valid_until ? v.certificate_valid_until.format('YYYY-MM-DD') : null,
        suitable_for_use: Boolean(v.suitable_for_use),
        limitations: v.limitations || null,
        remarks: v.remarks || null,
      },
    });
    verifyForm.resetFields();
  };

  const recordColumns: ColumnsType<CalibrationRecord> = [
    {
      title: 'Calibration no.',
      dataIndex: 'calibration_number',
      render: (v: string, r) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/grc/calibration/records/${r.id}`)}>
          {v}
        </Button>
      ),
    },
    { title: 'Date', dataIndex: 'calibration_date', render: (v) => formatDate(v, { nullDisplay: '—' }) },
    {
      title: 'Calibrated by',
      key: 'by',
      render: (_: unknown, r) => r.supplier_name || r.calibration_provider || r.calibrator_name || '—',
    },
    { title: 'Certificate', dataIndex: 'calibration_certificate_number', render: (v) => v || '—' },
    { title: 'Result', dataIndex: 'result', render: (v: string) => <Tag color={RESULT_COLORS[v]}>{v}</Tag> },
    {
      title: 'Quality review',
      dataIndex: 'quality_review_status',
      render: (v: string) => <Tag color={REVIEW_COLORS[v] || 'default'}>{v || '—'}</Tag>,
    },
    { title: 'Next due', dataIndex: 'next_calibration_due_date', render: (v) => formatDate(v, { nullDisplay: '—' }) },
  ];

  const scheduleColumns: ColumnsType<CalibrationSchedule> = [
    { title: 'Schedule', dataIndex: 'calibration_code' },
    { title: 'Type', dataIndex: 'calibration_kind', render: (v) => optionLabel(CALIBRATION_KIND_OPTIONS, v) },
    { title: 'Frequency (days)', dataIndex: 'calibration_frequency_days' },
    { title: 'Interval basis', dataIndex: 'interval_basis', render: (v) => optionLabel(INTERVAL_BASIS_OPTIONS, v) },
    { title: 'Due', dataIndex: 'calibration_due_date', render: (v) => formatDate(v, { nullDisplay: '—' }) },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag>{v}</Tag> },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: unknown, s) =>
        canEditSchedule && s.status !== 'Cancelled' ? (
          <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/grc/calibration/schedules/${s.id}/edit`)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  const verificationColumns: ColumnsType<TmmdeExternalVerification> = [
    { title: 'Verified', dataIndex: 'verified_at', render: (v) => formatDateTime(v) },
    { title: 'By', dataIndex: 'verified_by_name', render: (v) => v || '—' },
    { title: 'Certificate', dataIndex: 'certificate_reference', render: (v) => v || '—' },
    { title: 'Valid until', dataIndex: 'certificate_valid_until', render: (v) => formatDate(v, { nullDisplay: '—' }) },
    { title: 'Limitations', dataIndex: 'limitations', ellipsis: true, render: (v) => v || '—' },
    {
      title: 'Outcome',
      dataIndex: 'outcome',
      render: (v: string) => <Tag color={v === 'ACCEPTED' ? 'green' : 'red'}>{v}</Tag>,
    },
  ];

  const usageColumns: ColumnsType<TmmdeUsageEntry> = [
    { title: 'Used', dataIndex: 'used_at', render: (v) => formatDateTime(v) },
    {
      title: 'Used on',
      key: 'source',
      render: (_: unknown, u) => {
        const label = `${USAGE_SOURCE_LABEL[u.source_type] || u.source_type}${u.source_ref ? ` ${u.source_ref}` : ''}`;
        const link = usageSourceLink(u);
        return link ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(link)}>
            {label}
          </Button>
        ) : (
          label
        );
      },
    },
    { title: 'By', dataIndex: 'used_by_name', render: (v) => v || '—' },
    {
      title: 'Status at use',
      dataIndex: 'calibration_status_at_use',
      render: (v: TmmdeEquipmentRow['calibration_status']) => <Tag color={tmmdeStatusColor(v)}>{tmmdeStatusLabel(v)}</Tag>,
    },
    { title: 'Due at use', dataIndex: 'calibration_due_date_at_use', render: (v) => formatDate(v, { nullDisplay: '—' }) },
    {
      title: 'Override',
      key: 'override',
      render: (_: unknown, u) =>
        Number(u.override_used) ? (
          <Space direction="vertical" size={0}>
            <Tag color="red">Override</Tag>
            {u.override_reason && <Text type="secondary">{u.override_reason}</Text>}
          </Space>
        ) : (
          '—'
        ),
    },
  ];

  const tabs = [
    {
      key: 'records',
      label: `Calibration records (${data.records.length})`,
      children: (
        <Table
          rowKey="id"
          size="small"
          columns={recordColumns}
          dataSource={data.records}
          pagination={{ defaultPageSize: 10 }}
          expandable={{
            rowExpandable: (r) => (r.readings?.length ?? 0) > 0 || Boolean(r.review_remarks),
            expandedRowRender: (r) => (
              <Space direction="vertical" style={{ width: '100%' }}>
                {r.review_remarks && <Text type="secondary">Review remarks: {r.review_remarks}</Text>}
                {(r.readings?.length ?? 0) > 0 && (
                  <Table
                    rowKey={(x, i) => x.id || String(i)}
                    size="small"
                    pagination={false}
                    columns={readingColumns}
                    dataSource={r.readings}
                  />
                )}
              </Space>
            ),
          }}
        />
      ),
    },
    {
      key: 'schedules',
      label: `Schedules (${data.schedules.length})`,
      children: <Table rowKey="id" size="small" columns={scheduleColumns} dataSource={data.schedules} pagination={false} />,
    },
    {
      key: 'usage',
      label: `Usage (${usage.length})`,
      children: (
        <Table
          rowKey="id"
          size="small"
          loading={usageLoading}
          columns={usageColumns}
          dataSource={usage}
          pagination={{ defaultPageSize: 10 }}
          locale={{ emptyText: 'Not recorded on any inspection yet' }}
        />
      ),
    },
    ...(canViewImpact
      ? [
          {
            key: 'impact',
            label: `Impact assessments (${impactAssessments.length})`,
            children: (
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={impactAssessments}
                locale={{ emptyText: 'No impact assessments' }}
                columns={[
                  {
                    title: 'Assessment',
                    dataIndex: 'assessment_number',
                    render: (v: string, r) => (
                      <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/qms/tmmde/impact/${r.id}`)}>
                        {v}
                      </Button>
                    ),
                  },
                  { title: 'Trigger', dataIndex: 'trigger_type', render: (v: string) => IMPACT_TRIGGER_LABEL[v] || v },
                  { title: 'Opened', dataIndex: 'created_at', render: (v) => formatDateTime(v) },
                  {
                    title: 'Pending items',
                    key: 'pending',
                    render: (_: unknown, r) => `${Number(r.pending_count || 0)} / ${Number(r.item_count || 0)}`,
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    render: (v: string) => <Tag color={v === 'OPEN' ? 'orange' : 'green'}>{v === 'OPEN' ? 'Open' : 'Closed'}</Tag>,
                  },
                ] as ColumnsType<TmmdeImpactAssessment>}
              />
            ),
          },
        ]
      : []),
    {
      key: 'history',
      label: 'Status history',
      children: data.status_events.length ? (
        <Timeline
          items={data.status_events.map((e) => ({
            color: tmmdeStatusColor(e.to_status) === 'green' ? 'green' : ['red', 'volcano'].includes(tmmdeStatusColor(e.to_status)) ? 'red' : 'blue',
            children: (
              <Space direction="vertical" size={0}>
                <span>
                  {e.from_status ? `${tmmdeStatusLabel(e.from_status)} → ` : ''}
                  <Tag color={tmmdeStatusColor(e.to_status)}>{tmmdeStatusLabel(e.to_status)}</Tag>
                </span>
                {e.reason && <Text>{e.reason}</Text>}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatDateTime(e.changed_at)}
                  {e.changed_by_name ? ` · ${e.changed_by_name}` : ''}
                </Text>
              </Space>
            ),
          }))}
        />
      ) : (
        <Empty description="No status changes recorded yet" />
      ),
    },
    ...(external
      ? [
          {
            key: 'external',
            label: 'External-source verification',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Alert
                  type="info"
                  showIcon
                  message="Equipment from an external source"
                  description="Before use, verify the physical condition and a valid calibration certificate traceable to recognised standards, and record any limitations. A rejected verification quarantines the equipment."
                />
                <Table
                  rowKey="id"
                  size="small"
                  columns={verificationColumns}
                  dataSource={data.external_verifications}
                  pagination={false}
                  expandable={{
                    expandedRowRender: (v) => (
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {v.physical_condition_notes && <Text>Condition: {v.physical_condition_notes}</Text>}
                        {v.remarks && <Text>Remarks: {v.remarks}</Text>}
                        <EntityAttachmentsCard
                          entityType={DOCUMENT_ENTITY_TYPE_TMMDE_EXTERNAL_VERIFICATION}
                          entityId={v.id}
                          title="Certificate / evidence"
                          readOnly={!canManage}
                          queryKeyPrefix={['documents', 'tmmde-external-verification']}
                        />
                      </Space>
                    ),
                  }}
                />
                {canManage && (
                  <Card size="small" title="Record a verification">
                    <Form form={verifyForm} layout="vertical" onFinish={submitVerification}>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item name="physical_condition_ok" valuePropName="checked">
                            <Checkbox>Physical condition acceptable</Checkbox>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="certificate_verified" valuePropName="checked">
                            <Checkbox>Valid calibration certificate verified</Checkbox>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item name="suitable_for_use" valuePropName="checked">
                            <Checkbox>Suitable for the intended measurement</Checkbox>
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="certificate_reference" label="Certificate reference">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="certificate_valid_until" label="Certificate valid until">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24}>
                          <Form.Item name="physical_condition_notes" label="Condition notes">
                            <TextArea rows={2} />
                          </Form.Item>
                        </Col>
                        <Col xs={24}>
                          <Form.Item name="limitations" label="Limitations on use">
                            <TextArea rows={2} placeholder="e.g. range limited to 0–100 mm; not for final acceptance" />
                          </Form.Item>
                        </Col>
                        <Col xs={24}>
                          <Form.Item name="remarks" label="Remarks">
                            <TextArea rows={2} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Button type="primary" htmlType="submit" loading={verifyMutation.isPending}>
                        Save verification
                      </Button>
                      <Text type="secondary" style={{ marginLeft: 12 }}>
                        Attach the certificate on the saved row.
                      </Text>
                    </Form>
                  </Card>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/tmmde')}>
        Equipment List
      </Button>
      <Space style={{ width: '100%', justifyContent: 'space-between', margin: '8px 0 16px' }} wrap align="start">
        <Space direction="vertical" size={0}>
          <Title level={3} style={{ margin: 0 }}>
            {eq.asset_code} — {eq.asset_name}
          </Title>
          <Space size={8} wrap>
            <Tag color={tmmdeStatusColor(eq.calibration_status)} style={{ fontSize: 13 }}>
              {tmmdeStatusLabel(eq.calibration_status)}
            </Tag>
            {data.usable ? (
              <Text type="success">
                <CheckCircleOutlined /> Usable for inspection / test
              </Text>
            ) : (
              <Text type="danger">
                <WarningOutlined /> Not usable
              </Text>
            )}
          </Space>
        </Space>
        <Space wrap>
          <Button icon={<PrinterOutlined />} onClick={() => setLabelVariant('STATUS')}>
            Status label
          </Button>
          <Button icon={<PrinterOutlined />} danger onClick={() => setLabelVariant('NOT_FOR_USE')}>
            NOT FOR USE label
          </Button>
          {canChangeStatus && !onHold && (
            <>
              <Button danger icon={<StopOutlined />} onClick={() => setAction('NOT_FOR_USE')}>
                Not for use
              </Button>
              <Button onClick={() => setAction('QUARANTINE')}>Quarantine</Button>
            </>
          )}
          {canChangeStatus && onHold && (
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => setAction('RELEASE')}>
              Release
            </Button>
          )}
          {canCreateRecord && activeSchedule && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/grc/calibration/records/new?schedule=${activeSchedule.id}`)}
            >
              Record calibration
            </Button>
          )}
          {canCreateSchedule && !activeSchedule && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/grc/calibration/schedules/new?asset=${eq.id}`)}
            >
              Create schedule
            </Button>
          )}
          <Button onClick={() => navigate(`/asset/assets/${eq.id}`)}>Open asset</Button>
        </Space>
      </Space>

      {onHold && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${tmmdeStatusLabel(eq.calibration_status)} — do not use`}
          description={
            <>
              {eq.not_for_use_reason || 'No reason recorded.'} Affix the NOT FOR USE label and segregate the equipment.
              Release requires an accepted calibration (or a verification for external equipment).
            </>
          }
        />
      )}
      {eq.calibration_status === 'OVERDUE' && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Calibration overdue"
          description="Withdraw from use until recalibrated. Assess the validity of results measured since the last good calibration."
        />
      )}
      {!activeSchedule && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="No active calibration schedule"
          description="Define the calibration / verification method, interval and acceptance criteria."
        />
      )}

      <Card title="Equipment profile" style={{ marginBottom: 16 }}>
        <Descriptions bordered size="small" column={{ xs: 1, md: 2, xl: 3 }}>
          <Descriptions.Item label="ID">{eq.asset_code}</Descriptions.Item>
          <Descriptions.Item label="Serial no.">{eq.serial_number || '—'}</Descriptions.Item>
          <Descriptions.Item label="Manufacturer / model">
            {[eq.manufacturer, eq.model].filter(Boolean).join(' · ') || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Ownership">{optionLabel(OWNERSHIP_OPTIONS, eq.equipment_ownership)}</Descriptions.Item>
          <Descriptions.Item label="Identification">
            {optionLabel(IDENTIFICATION_METHOD_OPTIONS, eq.identification_method)}
          </Descriptions.Item>
          <Descriptions.Item label="Location">
            {eq.warehouse_name || [eq.site, eq.building, eq.room].filter(Boolean).join(' / ') || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Working range">{eq.working_range || '—'}</Descriptions.Item>
          <Descriptions.Item label="Accuracy">{eq.accuracy || '—'}</Descriptions.Item>
          <Descriptions.Item label="Unit">{eq.measurement_unit || '—'}</Descriptions.Item>
          <Descriptions.Item label="Type">{optionLabel(CALIBRATION_KIND_OPTIONS, eq.calibration_kind)}</Descriptions.Item>
          <Descriptions.Item label="Frequency">
            {eq.calibration_frequency_days ? `${eq.calibration_frequency_days} days` : '—'}
            {eq.interval_basis === 'FIRST_USE' ? ' (from first use)' : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Calibrated by">
            {eq.supplier_name || eq.calibration_provider || eq.calibration_type || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Last calibration">{formatDate(eq.last_calibration_date, { nullDisplay: '—' })}</Descriptions.Item>
          <Descriptions.Item label="Due date">
            {eq.calibration_due_date
              ? formatDate(eq.calibration_due_date)
              : eq.interval_basis === 'FIRST_USE'
                ? 'Starts on first use'
                : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Critical">{Number(eq.is_critical) ? 'Yes' : 'No'}</Descriptions.Item>
          <Descriptions.Item label="Acceptance criteria" span={3}>
            {eq.acceptance_criteria || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Reference standard / method" span={3}>
            {eq.calibration_standard || '—'}
          </Descriptions.Item>
          {activeSchedule?.external_limitations && (
            <Descriptions.Item label="Limitations on use" span={3}>
              <Text type="warning">{activeSchedule.external_limitations}</Text>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card>
        <Tabs items={tabs} />
      </Card>

      <Modal
        open={Boolean(action)}
        title={action ? ACTION_COPY[action].title : ''}
        okText={action ? ACTION_COPY[action].okText : 'OK'}
        okButtonProps={{
          danger: action !== 'RELEASE',
          disabled: Boolean(action && ACTION_COPY[action].reasonRequired && !reason.trim()),
          loading: statusMutation.isPending,
        }}
        onOk={() => void submitAction()}
        onCancel={() => {
          setAction(null);
          setReason('');
        }}
        destroyOnHidden
      >
        {action === 'RELEASE' && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Release is only allowed when the equipment has a valid accepted calibration (and an accepted verification for external equipment)."
          />
        )}
        {action === 'NOT_FOR_USE' && (
          <>
            <Select
              style={{ width: '100%', marginBottom: 12 }}
              value={cause}
              onChange={setCause}
              options={HOLD_CAUSE_OPTIONS}
            />
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="If this equipment was used since its last accepted calibration, an impact assessment is opened for the affected inspections, work orders, deliveries and customers."
            />
          </>
        )}
        <TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={action && ACTION_COPY[action].reasonRequired ? 'Reason (required)' : 'Remarks (optional)'}
        />
      </Modal>
    </div>
  );
};

export default TmmdeEquipmentDetailPage;
